import { isNullOrUndefined, isNullUndefinedOrWhiteSpace } from "./any";
import type { QueryStringParam } from "./http/query";

export async function downloadBlob(response: Response, openInNewTab = false) {
  const filename = getContentDispositionFilename(response.headers.get("Content-Disposition"));
  if (!filename) {
    throw new Error("Unable to parse filename.");
  }

  const blob = await response.blob();
  const file = new File([blob], filename, { type: "application/octet-stream" });
  const downloadUrl = URL.createObjectURL(file);

  if (openInNewTab) {
    window.open(downloadUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(downloadUrl), 5000);
    return;
  }

  const a = document.createElement("a");
  a.style.display = "none";
  a.href = downloadUrl;
  a.download = filename;
  document.body.appendChild(a);
  setTimeout(() => a.click(), 0);
  setTimeout(() => {
    URL.revokeObjectURL(downloadUrl);
    a.remove();
  }, 5000);
}

export function getUrlParam(name: string, url: string): string | null {
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(`([?&])${escapedName}=`, "i");
  const match = pattern.exec(url);
  if (!match) {
    return null;
  }

  const valueStart = match.index + match[0].length;
  let nextDelimiter = url.indexOf("&", valueStart);
  const hashIndex = url.indexOf("#", valueStart);
  let fragment = "";

  if (hashIndex !== -1 && (nextDelimiter === -1 || hashIndex < nextDelimiter)) {
    fragment = url.slice(hashIndex);
    nextDelimiter = hashIndex;
  }

  const rawValue = url.slice(valueStart, nextDelimiter === -1 ? url.length : nextDelimiter);
  return `${decodeURIComponent(rawValue)}${fragment}`;
}

export function extractQueryParams(url: string): Record<string, string> {
  const urlObj = new URL(url);

  const pathSegments = urlObj.pathname.split("/");
  const path = pathSegments[pathSegments.length - 1];

  const params: Record<string, string> = {};

  if (!isNullUndefinedOrWhiteSpace(path)) {
    params.path = path;
  }

  urlObj.searchParams.forEach((value, key) => {
    params[key] = value;
  });

  return params;
}

export type { QueryStringParam } from "./http/query";

export function getContentDispositionFilename(value: string | null): string | null {
  if (!value) return null;

  const parts = value
    .split(";")
    .map(part => part.trim())
    .filter(Boolean);

  let fallback: string | undefined;

  for (const part of parts) {
    const [rawKey, ...rawValueParts] = part.split("=");
    if (!rawKey || rawValueParts.length === 0) {
      continue;
    }

    const key = rawKey.trim().toLowerCase();
    const rawValue = rawValueParts.join("=").trim();

    if (key === "filename*" || key === "filename_") {
      const valueCandidate = decodeContentDispositionValue(rawValue, true);
      if (valueCandidate) {
        return valueCandidate;
      }
    }

    if (!fallback && key === "filename") {
      fallback = decodeContentDispositionValue(rawValue, false);
    }
  }

  return fallback ?? null;
}

export function navigateTo(url: string) {
  return new Promise<void>(resolve => {
    window.addEventListener("unload", () => resolve());
    window.location.assign(url);
  });
}

export function buildQueryString(values: Record<string, QueryStringParam>) {
  return `?${buildQueryStringKeyValuePairs(Object.entries(values))}`;
}

function buildQueryStringKeyValuePairs(values: [string, QueryStringParam][]): string {
  return values
    .filter(([, value]) => !isNullOrUndefined(value))
    .map(([key, value]) =>
      Array.isArray(value)
        ? buildQueryStringKeyValuePairs(value.map(x => [key, x]))
        : `${key}=${encodeURIComponent(value ?? "")}`
    )
    .join("&");
}

function decodeContentDispositionValue(value: string, encoded: boolean) {
  const unquoted = value.startsWith('"') && value.endsWith('"') ? value.slice(1, -1) : value;
  if (encoded) {
    const encodingMatch = /^([^']*)''(.*)$/.exec(unquoted);
    if (encodingMatch) {
      const encodedValue = encodingMatch[2];
      return decodePercentEncodedValue(encodedValue);
    }
  }

  return decodePercentEncodedValue(unquoted);
}

function decodePercentEncodedValue(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
